import { SimilarDesignItem } from '@app/data/clients/rbServerSideClient/queries/GetSimilarDesigns/types';
import { Shop_IaEntityCopy } from '@app/data/shop-bff';
import { AnalyticsComponentName } from '@app/pages/i/analytics';
import { SIMILAR_DESIGNS_SEO_LINK_LIMIT } from '@app/pages/i/constants';
import { constructShopTagSlug } from '@app/utils/common';
import Box from '@redbubble/design-system/src/Box';
import Button from '@redbubble/design-system/src/Button';
import * as constants from '@redbubble/design-system/src/constants';
import GridIcon from '@redbubble/design-system/src/Icons/Grid';
import { FormattedMessage } from '@redbubble/next/intl';
import { AppProps } from '@redbubble/next/types';
import {
  type Item as AnalyticsEcommerceItem,
  AnalyticsItemsList,
} from '@redbubble/next-analytics/gtm';
import { usePageProps } from '@redbubble/next-provider';
import {
  RSPageType,
  RSProductClickedLocation,
} from '@redbubble/next-provider/rudderstack';
import chunk from 'lodash/chunk';
import React, { memo, useEffect, useMemo } from 'react';
import { CarouselSection } from '../../../../components/CarouselSection';
import {
  CarouselSectionContext,
  CarouselSectionContextValue,
} from '../../../../components/CarouselSection/carouselSectionContext';
import { useListsInclusions } from '../../../../hooks/useListsInclusions';
import { SearchUUIDStorageKey } from '../../../../utils/searchUUIDStorage';
import {
  parseSimilarDesignItemToAnalyticsEcommerceItem,
  trackGA4ViewItemListEvent,
} from '../../analytics';
import { ProductPageProps } from '../../types';
import styles from './SimilarDesignsSection.module.scss';
import { SimilarDesignsSectionHeader } from './SimilarDesignsSectionHeader';
import {
  getUsableSimilarDesigns,
  mapMediaCardProps,
  mapPriceProps,
} from './utils';

interface SimilarDesignsCarouselProps {
  iaEntityCopy: Shop_IaEntityCopy | null;
  firstTag: string | null;
  similarDesignsLoading: boolean;
  similarDesigns: SimilarDesignItem[];
}

export const SimilarDesignsSection: React.FC<SimilarDesignsCarouselProps> =
  memo(({ iaEntityCopy, firstTag, similarDesignsLoading, similarDesigns }) => {
    const { groupId } = usePageProps<AppProps & ProductPageProps>();

    const { listsInclusions, refresh: refreshListInclusions } =
      useListsInclusions();

    useEffect(() => {
      if (!similarDesignsLoading)
        refreshListInclusions(
          similarDesigns.map(({ inventoryItem }) => inventoryItem)
        );
    }, [similarDesigns, refreshListInclusions, similarDesignsLoading]);

    const contextValue: CarouselSectionContextValue = useMemo(() => {
      return {
        mediaCardPropsMapper: mapMediaCardProps(
          listsInclusions,
          groupId,
          SIMILAR_DESIGNS_SEO_LINK_LIMIT
        ),
        pricePropsMapper: mapPriceProps,
        analytics: {
          componentName: AnalyticsComponentName.SimilarDesigns,
          listID: AnalyticsItemsList.SimilarDesignsCarousel,
          inViewTracking: () => {
            const analyticsItems = similarDesigns
              .map((similarDesignItem, index) => {
                return parseSimilarDesignItemToAnalyticsEcommerceItem({
                  similarDesignItem,
                  index,
                });
              })
              .filter(
                (
                  item: AnalyticsEcommerceItem | null
                ): item is NonNullable<AnalyticsEcommerceItem> => !!item
              );

            trackGA4ViewItemListEvent({
              items: analyticsItems,
              listID: AnalyticsItemsList.SimilarDesignsCarousel,
              topic: '',
              searchUUIDStorageKey: SearchUUIDStorageKey.LocationPathname,
            });
          },
        },
        rsLocation: RSProductClickedLocation.SimilarDesignsCarousel,
        rsPageType: RSPageType.Product,
      };
    }, [listsInclusions, similarDesigns, groupId]);

    const getSlides = () => {
      const chunkSize = 2;

      // TODO[DEP-2566]: Skeleton loading state.
      if (!similarDesignsLoading && similarDesigns.length > 0) {
        return chunk(getUsableSimilarDesigns(similarDesigns), chunkSize).map(
          (recommendations: SimilarDesignItem[], outerIndex: number) => {
            return (
              <CarouselSection.CarouselSlide
                key={`sd-slide-${recommendations
                  .map((r: SimilarDesignItem) => {
                    return r.inventoryItemId;
                  })
                  .join('+')}`}
              >
                {recommendations.map(
                  (recommendation: SimilarDesignItem, innerIndex: number) => (
                    <CarouselSection.CarouselMediaCard
                      data={recommendation}
                      key={recommendation.inventoryItemId}
                      index={innerIndex + chunkSize * outerIndex}
                    />
                  )
                )}
              </CarouselSection.CarouselSlide>
            );
          }
        );
      }

      return null;
    };

    const viewMoreDesignsSlug =
      firstTag &&
      iaEntityCopy &&
      constructShopTagSlug(firstTag, iaEntityCopy.canonicalForm);

    // In case of errors, hook returns empty array
    if (similarDesigns.length === 0) {
      return null;
    }

    return (
      <CarouselSectionContext.Provider value={contextValue}>
        <CarouselSection.Container>
          <Box className={styles.container}>
            <Box className={styles.headerSection}>
              <SimilarDesignsSectionHeader
                itemLabel={iaEntityCopy && iaEntityCopy.pluralDisplayName}
              />
            </Box>
            {firstTag && viewMoreDesignsSlug && (
              <Box className={styles.viewMoreButton}>
                <Button
                  intent={constants.PRIMARY}
                  size={constants.SMALL}
                  iconBefore={<GridIcon size={constants.SMALL} />}
                  href={viewMoreDesignsSlug}
                >
                  <FormattedMessage defaultMessage="View more similar designs" />
                </Button>
              </Box>
            )}
            <Box className={styles.carousel}>
              <CarouselSection.Carousel>{getSlides()}</CarouselSection.Carousel>
            </Box>
          </Box>
        </CarouselSection.Container>
      </CarouselSectionContext.Provider>
    );
  });
